/**
 * TrustLivePostureTile.jsx — Phase 12.8 · Public defence-posture rollup.
 *
 * Renders the snapshot from `GET /api/public/trust/posture` as one
 * "Live security posture" tile on the Trust Center. Each defence
 * layer (audit chain, R2 cold archive, backup drill, subdomain sweep,
 * email posture, supply-chain audit, edge defence, WAF sync) gets a
 * single row with a status pip + the most relevant time-since metric.
 *
 * Strict no-leak policy: this component renders ONLY whatever the
 * public endpoint already chose to expose. We never reach for internal
 * detail directly — that gate is the API's job.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion,
  GitMerge, Archive, RotateCcw, Network, Mail, Package, AlertTriangle,
  Server, Clock, RefreshCw,
} from "lucide-react";


const API = process.env.REACT_APP_BACKEND_URL;


/* ─── Style maps ────────────────────────────────────────────────── */

const LEVEL_META = {
  green:   { color: "#1FDD83", label: "All green",   icon: ShieldCheck    },
  yellow:  { color: "#FFB000", label: "Attention",   icon: ShieldAlert    },
  red:     { color: "#FF6E6E", label: "Action req.", icon: ShieldX        },
  unknown: { color: "#7F8FA0", label: "Unknown",     icon: ShieldQuestion },
};

const CHECK_META = {
  audit_chain:     { icon: GitMerge,        title: "Hash-chained audit log" },
  r2_archive:      { icon: Archive,         title: "Cold-archive cron"      },
  backup_drill:    { icon: RotateCcw,       title: "Backup-restore drill"   },
  subdomain_sweep: { icon: Network,         title: "Subdomain takeover sweep" },
  email_posture:   { icon: Mail,            title: "Email auth (SPF/DKIM/DMARC)" },
  supply_chain:    { icon: Package,         title: "Supply-chain audit"     },
  under_attack:    { icon: AlertTriangle,   title: "Edge defence posture"   },
  waf_sync:        { icon: Server,          title: "WAF rules synced"       },
};


/* ─── Helpers ───────────────────────────────────────────────────── */

function _fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const sec = (Date.now() - d.getTime()) / 1000;
    if (sec < 60)     return "just now";
    if (sec < 3600)   return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400)  return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch { return iso; }
}

function _pickHint(key, c) {
  if (!c) return null;
  if (c.status === "unknown" && c.error) return "scan errored";
  if (c.note) return c.note;
  switch (key) {
    case "audit_chain":
      return c.entries_total != null
        ? `${c.entries_total} entries · checked ${c.entries_checked ?? 0}`
        : null;
    case "r2_archive":
      return c.days_since_archive != null
        ? `${c.days_since_archive}d since · ${c.rows ?? 0} rows`
        : null;
    case "backup_drill":
      return c.days_since != null
        ? `${c.days_since}d since last drill`
        : null;
    case "subdomain_sweep":
      return c.domains_checked != null
        ? `${c.domains_checked} domains scanned · ${c.vulnerable_count ?? 0} flagged`
        : null;
    case "email_posture":
      return c.domains_total
        ? `${c.domains_ok}/${c.domains_total} domains OK`
        : null;
    case "supply_chain":
      return c.days_since_clean != null
        ? `clean for ${c.days_since_clean}d`
        : null;
    case "under_attack":
      return c.under_attack ? "Under Attack mode ON" : "Calm";
    case "waf_sync":
      return c.last_sync_at ? `synced ${_fmtRelative(c.last_sync_at)}` : null;
    default:
      return null;
  }
}


/* ─── Component ─────────────────────────────────────────────────── */

export default function TrustLivePostureTile() {
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState("");
  const [refreshedAt, setRefreshedAt] = useState(null);

  const fetchOnce = async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/trust/posture`);
      setSnap(data);
      setRefreshedAt(new Date());
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load live posture.");
    }
  };

  useEffect(() => {
    fetchOnce();
    const id = setInterval(fetchOnce, 60_000);   // mirror server cache
    return () => clearInterval(id);
  }, []);

  if (error) {
    return (
      <div
        className="rounded-md border border-rose-700 bg-rose-900/30 p-5 text-sm text-rose-200"
        data-testid="trust-live-posture-error"
      >
        <AlertTriangle className="mr-2 inline h-4 w-4" />
        {error}
      </div>
    );
  }

  if (!snap) {
    return (
      <div
        className="rounded-md border border-gray-800 bg-[#0E1219] p-6 text-sm text-gray-400"
        data-testid="trust-live-posture-loading"
      >
        Loading live security posture…
      </div>
    );
  }

  const level = LEVEL_META[snap.level] || LEVEL_META.unknown;
  const LevelIcon = level.icon;

  return (
    <section
      className="rounded-md border border-[#1FDD83]/30 bg-[#0E1219] p-6"
      data-testid="trust-live-posture-tile"
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1FDD83]">
            Live security posture
            <span className="text-gray-500 normal-case">·</span>
            <span
              className="font-mono text-gray-500"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
            >
              {snap.policy_version}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-bold uppercase tracking-wider"
              style={{
                color: level.color,
                borderColor: level.color,
                background: `${level.color}1A`,
              }}
              data-testid="trust-live-posture-headline"
            >
              <LevelIcon className="h-3.5 w-3.5" />
              {level.label}
            </span>
            <span className="text-xs text-gray-500">
              Last refreshed {_fmtRelative(snap.scanned_at)}
            </span>
          </div>
        </div>
        <button
          onClick={fetchOnce}
          className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-transparent px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-gray-400 transition-colors hover:border-white/30 hover:text-white"
          data-testid="trust-live-posture-refresh"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* ── Layered checks ─────────────────────────────────────── */}
      <ul
        className="grid gap-2 sm:grid-cols-2"
        data-testid="trust-live-posture-checks"
      >
        {Object.entries(snap.checks || {}).map(([key, c]) => {
          const meta = CHECK_META[key] || { icon: ShieldQuestion, title: key };
          const stat = LEVEL_META[c.status] || LEVEL_META.unknown;
          const StatIcon = stat.icon;
          const Icon = meta.icon;
          const hint = _pickHint(key, c);
          return (
            <li
              key={key}
              data-testid={`trust-live-posture-row-${key}`}
              className="flex items-center gap-3 rounded-md border border-white/5 bg-black/30 p-3"
            >
              <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-gray-200">
                  {c.label || meta.title}
                </div>
                {hint && (
                  <div className="text-[11px] text-gray-500">
                    {hint}
                  </div>
                )}
              </div>
              <span
                className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                style={{
                  color: stat.color,
                  borderColor: stat.color,
                  background: `${stat.color}1A`,
                }}
                data-testid={`trust-live-posture-status-${key}`}
              >
                <StatIcon className="h-3 w-3" /> {c.status}
              </span>
            </li>
          );
        })}
      </ul>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <div className="mt-5 flex items-center gap-2 text-[11px] text-gray-500">
        <Clock className="h-3 w-3" />
        Auto-refreshes every 60 seconds. Refresh interval matches the
        server-side cache so we never hammer Mongo.
      </div>
    </section>
  );
}
